/**
 * Create Business Capability Route
 *
 * Form for creating new business capabilities
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { BusinessCapabilityForm } from '../../components/capabilities';
import {
  createCapability,
  ApiError,
  type CreateBusinessCapabilityInput,
} from '../../lib/apiClient';
import type { CreateCapabilityFormData } from '../../components/forms/validation';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';
import './new.css';

export const Route = createFileRoute('/capabilities/new')({
  component: CreateCapabilityPage,
});

function CreateCapabilityPage(): JSX.Element {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  const handleSubmit = async (data: CreateCapabilityFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const input: CreateBusinessCapabilityInput = {
        name: data.name,
        description: data.description,
        level: data.level,
        parentCapabilityId: data.parentCapabilityId || undefined,
        criticality: data.criticality,
        strategicImportance: data.strategicImportance,
        lifecycleStatus: data.lifecycleStatus,
        investmentPriority: data.investmentPriority || undefined,
        businessOwnerId: data.businessOwnerId,
        owningBusinessUnitId: data.owningBusinessUnitId,
        stakeholderIds: data.stakeholderIds,
        maturityLevel: data.maturityLevel,
        targetMaturityLevel: data.targetMaturityLevel,
        currentInvestmentLevel: data.currentInvestmentLevel || undefined,
        targetInvestmentLevel: data.targetInvestmentLevel || undefined,
        planningHorizon: data.planningHorizon || undefined,
        notes: data.notes || undefined,
        assumptions: data.assumptions || undefined,
        tags: data.tags,
      };

      const capability = await createCapability(input);
      navigate({
        to: '/capabilities/$capabilityId',
        params: { capabilityId: capability.entityId },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/capabilities', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="create-capability-page">
      <div className="breadcrumb">
        <Link to="/capabilities" search={{ page: 1, pageSize: 25 }} className="breadcrumb-link">
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to Business Capabilities</span>
        </Link>
      </div>

      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Business Capability</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Add a new business capability to the EA catalog
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <BusinessCapabilityForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

export { CreateCapabilityPage };
